import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

type DropdownContextValue = {
  isOpen: boolean;
  setIsOpen: (next: boolean) => void;
  toggle: () => void;
  labelledById?: string;
  setLabelledById: (id: string | undefined) => void;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
};

const DropdownContext = createContext<DropdownContextValue | null>(null);

function useDropdownContext(): DropdownContextValue {
  const ctx = useContext(DropdownContext);
  if (!ctx) {
    throw new Error('Dropdown components must be used within <Dropdown>.');
  }
  return ctx;
}

export interface DropdownProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: React.ReactNode;
  isOpen?: boolean;
  toggle?: () => void;
}

export const Dropdown: React.FC<DropdownProps> = ({
  className = '',
  children,
  isOpen,
  toggle,
  ...props
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = isOpen ?? uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (isOpen !== undefined) {
        toggle?.();
        return;
      }
      setUncontrolledOpen(next);
    },
    [isOpen, toggle]
  );

  const doToggle = useCallback(() => {
    if (toggle) {
      toggle();
      return;
    }
    setOpen(!open);
  }, [open, setOpen, toggle]);

  const [labelledById, setLabelledById] = useState<string | undefined>(undefined);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setOpen(false), [setOpen]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target instanceof Node ? e.target : null;
      if (!target) return;
      const root = (buttonRef.current?.closest('[data-tw-dropdown-root="true"]') ?? null) as HTMLElement | null;
      if (!root) return;
      if (!root.contains(target)) close();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [close, open]);

  const value = useMemo<DropdownContextValue>(
    () => ({
      isOpen: open,
      setIsOpen: setOpen,
      toggle: doToggle,
      labelledById,
      setLabelledById,
      buttonRef,
    }),
    [doToggle, labelledById, open, setOpen]
  );

  return (
    <DropdownContext.Provider value={value}>
      <div
        data-tw-dropdown-root="true"
        className={`relative inline-block text-left ${className}`}
        {...props}
      >
        {children}
      </div>
    </DropdownContext.Provider>
  );
};

export interface DropdownToggleProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  children: React.ReactNode;
}

export const DropdownToggle: React.FC<DropdownToggleProps> = ({
  className = '',
  children,
  type = 'button',
  ...props
}) => {
  const { isOpen, toggle, setLabelledById, buttonRef } = useDropdownContext();
  const id = useId();

  useEffect(() => {
    setLabelledById(id);
    return () => setLabelledById(undefined);
  }, [id, setLabelledById]);

  return (
    <button
      ref={buttonRef}
      id={id}
      type={type}
      aria-haspopup="menu"
      aria-expanded={isOpen}
      onClick={(e) => {
        props.onClick?.(e);
        if (e.defaultPrevented) return;
        toggle();
      }}
      className={className}
      {...props}
    >
      {children}
    </button>
  );
};

export interface DropdownMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: React.ReactNode;
  end?: boolean;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  className = '',
  children,
  end = false,
  ...props
}) => {
  const { isOpen, labelledById } = useDropdownContext();

  if (!isOpen) return null;

  const placement = end ? 'right-0' : 'left-0';

  return (
    <div
      role="menu"
      aria-labelledby={labelledById}
      className={`absolute z-50 mt-2 min-w-[12rem] rounded-md border border-[var(--color-gray-200)] bg-white shadow-lg focus:outline-none ${placement} ${className}`}
      {...props}
    >
      <ul className="py-1">{children}</ul>
    </div>
  );
};

export interface DropdownItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const DropdownItem: React.FC<DropdownItemProps> = ({
  className = '',
  children,
  ...props
}) => {
  const { setIsOpen } = useDropdownContext();

  return (
    <li role="none">
      <button
        type="button"
        role="menuitem"
        className={`w-full px-4 py-2 text-left text-sm text-[var(--color-gray-800)] hover:bg-[var(--color-gray-100)] focus:bg-[var(--color-gray-100)] ${className}`}
        onClick={(e) => {
          props.onClick?.(e);
          if (!e.defaultPrevented) setIsOpen(false);
        }}
        {...props}
      >
        {children}
      </button>
    </li>
  );
};

export interface UncontrolledDropdownProps extends Omit<DropdownProps, 'isOpen' | 'toggle'> {}

export const UncontrolledDropdown: React.FC<UncontrolledDropdownProps> = (props) => {
  return <Dropdown {...props} />;
};
